/**
 * Understaffed in the coming days (#2175 D4), pure. Port of `groupByDay` and
 * the window of mobile-management/src/features/team/UnderstaffedAheadCard.tsx
 * and team/dateRanges.ts (`daysAhead`): tomorrow through today + 7, groups per
 * date with critical first, then tight, each by name.
 */

export interface UnderstaffedGroup {
  date?: string;
  group_id?: string;
  group_name?: string;
  status?: string;
}

export interface UnderstaffedDay<G extends UnderstaffedGroup = UnderstaffedGroup> {
  date: string;
  groups: G[];
}

export const UNDERSTAFFED_AHEAD_DAYS = 7;

function shift(day: string, days: number): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** Tomorrow through `today` + `days` (today has its own figure). */
export function daysAhead(today: string, days: number): { from: string; to: string } {
  return { from: shift(today, 1), to: shift(today, days) };
}

export function groupByDay<G extends UnderstaffedGroup>(
  groups: readonly G[] | undefined,
  matchesGroup: (groupId?: string | null) => boolean,
): UnderstaffedDay<G>[] {
  const byDate = new Map<string, G[]>();
  for (const g of groups ?? []) {
    if (g.status !== "CRITICAL" && g.status !== "WARNING") continue;
    if (!g.date || !matchesGroup(g.group_id)) continue;
    const list = byDate.get(g.date) ?? [];
    list.push(g);
    byDate.set(g.date, list);
  }
  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, list]) => ({
      date,
      groups: list.sort(
        (a, b) =>
          Number(b.status === "CRITICAL") - Number(a.status === "CRITICAL") ||
          (a.group_name ?? "").localeCompare(b.group_name ?? ""),
      ),
    }));
}
